import type { StaffReport, StaffRow } from '#shared/types';
import { BarChart } from '#client/components/charts';
import { Banner } from '#client/components/ui/Banner';
import { Card } from '#client/components/ui/Card';
import { type Column, DataTable } from '#client/components/ui/DataTable';
import { SectionShell } from './SectionShell';
import { formatterFor } from './format';
import { formatStaffMedian, heightForRows } from './report.logic';
import { toStaffOutputSeries } from './to-series';
import { useAnalyticsFilters } from './useAnalyticsFilters';
import { useAnalyticsSection } from './useAnalyticsSection';

/**
 * Who did what.
 *
 * A technician gets their own row and the lab total; an admin gets everybody.
 * The screen says which of those it is showing, rather than letting a
 * single-row table imply the lab employs one person.
 *
 * The two caveats on the columns are not decoration. `performed` is credited to
 * whoever saved the first result and is never reassigned; `verified` is cleared
 * when a report is reopened, so it can go *down*. Both are stated where the
 * numbers are, because a productivity figure read without them is a
 * productivity figure read wrongly.
 */
export function StaffPage() {
  const filters = useAnalyticsFilters('staff');
  const { data, loading, error } = useAnalyticsSection<StaffReport>('staff', filters);

  return (
    <SectionShell
      section="staff"
      filters={filters}
      data={data}
      loading={loading}
      error={error}
      metrics={data?.headline}
      empty={data !== undefined && data.rows.length === 0}
      emptyMessage="Nothing was registered, entered or verified in this period."
    >
      {data && (
        <div className="c-an__grid">
          {data.own_row_only && <OwnRowBanner />}
          <OutputCard rows={data.rows} />
          <StaffTableCard rows={data.rows} />
          <StaffCaveats />
        </div>
      )}
    </SectionShell>
  );
}

function OwnRowBanner() {
  return (
    <div className="c-an__cell c-an__cell--wide">
      <Banner tone="info">
        Showing your own figures. The lab-wide totals are above, so you can see how your work sits
        against the whole.
      </Banner>
    </div>
  );
}

function OutputCard({ rows }: { rows: readonly StaffRow[] }) {
  return (
    <Card
      className="c-an__cell c-an__cell--wide"
      title="Output"
      subtitle="Results entered and reports verified"
    >
      <BarChart
        title="Results entered and reports verified per person"
        orientation="horizontal"
        mode="grouped"
        series={toStaffOutputSeries(rows)}
        format={formatterFor('count')}
        height={heightForRows(rows.length, 56, 180)}
        categoryLabel="Person"
      />
    </Card>
  );
}

/** A right-aligned count column. */
function countColumn(
  key: 'registered' | 'performed' | 'verified',
  header: string,
): Column<StaffRow> {
  return {
    key,
    header,
    align: 'end',
    render: (row) => <span className="tnum">{row[key]}</span>,
  };
}

const STAFF_COLUMNS: Column<StaffRow>[] = [
  { key: 'name', header: 'Person', render: (row) => row.name },
  { key: 'role', header: 'Role', render: (row) => row.role },
  countColumn('registered', 'Visits registered'),
  countColumn('performed', 'Results entered'),
  countColumn('verified', 'Reports verified'),
  {
    key: 'p50',
    header: 'Median turnaround',
    align: 'end',
    render: (row) => <span className="tnum">{formatStaffMedian(row)}</span>,
  },
];

function StaffTableCard({ rows }: { rows: StaffRow[] }) {
  return (
    <Card className="c-an__cell c-an__cell--wide" title="The numbers" flush>
      <DataTable
        caption="Output per person"
        columns={STAFF_COLUMNS}
        rows={rows}
        rowKey={(row) => row.user_id}
      />
    </Card>
  );
}

function StaffCaveats() {
  return (
    <div className="c-an__cell c-an__cell--wide">
      <ul className="c-an-notes">
        <li className="c-an-notes__item">
          Results entered are credited to whoever saved the first result on a report. A later
          correction by somebody else does not move it.
        </li>
        <li className="c-an-notes__item">
          Reopening a report clears its verifier, so this count can fall as well as rise — and the
          original verification is not recorded anywhere else.
        </li>
      </ul>
    </div>
  );
}
